/* eslint-disable no-console */
import {FC, PropsWithChildren} from "react";
import {ScriptStatus} from "./ScriptStatus";
import {DefaultLoadingIndicator} from "./DefaultLoadingIndicator";
import {DefaultFailureIndicator} from "./DefaultFailureIndicator";
import {ScriptReference} from "./ScriptReference";
import {ScriptPlaceholderProps} from "./ScriptPlaceholderProps";
import {useScriptStatus} from "./useScriptStatus";

export type ScriptObserverProps = ScriptPlaceholderProps & ScriptReference;

/**
 * The {@link ScriptObserver} is designed to be used when a script has been
 * injected elsewhere withing an application by one of the following methods:
 * <ul>
 *     <li>The useScript hook</li>
 *     <li>The Script component</li>
 *     <li>The ScriptLoader component</li>
 * </ul>
 *
 * As such, it is not responsible for the actual loading of a script, it is
 * only interested in observing a previously defined script. This is useful
 * in situations where a single script may contain multiple custom elements.
 *
 * @param id
 * @param loading
 * @param failure
 * @param children
 * @constructor
 * @see useScript
 * @see Script
 * @see ScriptLoader
 */
export const ScriptObserver: FC<PropsWithChildren<ScriptObserverProps>> = ({
  id,
  loading = DefaultLoadingIndicator,
  failure = DefaultFailureIndicator,
  children
}) => {
  const Loading = loading;
  const Failure = failure;
  const status = useScriptStatus(id);
  console.info(`Rendering script ${id} with status ${status}`);
  switch (status) {
    case ScriptStatus.LOADED:
      return <>{children}</>;
    case ScriptStatus.LOADING:
      return <Loading scriptId={id} />;
    case ScriptStatus.FAILED:
      return <Failure scriptId={id} />;
  }
};
